/* Tab Báo giá & cọc trong hồ sơ khách tiềm năng: lịch sử báo giá + tạo báo giá (dịch vụ · ưu đãi · gửi khách · yêu cầu cọc).
   Khách thật lưu trên server, server cấp mã và tự ghi lịch sử chạm; khách mẫu và báo giá cũ không có serverId vẫn xử lý trên máy. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Empty, Input, InputNumber, Popconfirm, Segmented, Select, Switch, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { Banknote, CalendarPlus, CreditCard, FilePlus2, Send } from 'lucide-react';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import { createDeposit, createQuote, quoteAct, reloadQuotes, reloadTouches, runDepositApi, updateQuote, type QuoteBody, emitCustomerPatched } from './api';
import { Field } from './BookingFields';
import { isDemoId } from './demo';
import { depositFromServer } from './deposit';
import { DICH_VUS, dichVuOf, uid, type DichVu, type LeadLocal, type Mang } from './logic';
import {
  DEP_PURPOSE, DEP_ST, DV_SEARCH, NGUONG_GIAM, PROMOS, QUOTE_ST, SEND_CH, cardSummary, checkQuote, daysLeft, depositNote, isLive, orderDue, markSent, money,
  nextQuoteCode, quoteMessage, type DepPurpose, type Deposit, type Quote, type QuoteSvc, type SendCh,
} from './quote';
import { searchServices, type SvcOpt } from './apptRefs';
import { useLeadStore } from './store';
import { cardEmptyText, cardOptions, useCardPackages } from '../../AppTuVan/packages';

/** Kênh chat: tin báo giá hiện luôn trong tab Nhắn tin */
const CHAT_CH: SendCh[] = ['Zalo', 'Messenger', 'WhatsApp'];
const DEP_AMOUNTS = [500_000, 1_000_000, 2_000_000];
const fmtNum = (v?: number | string) => (v == null || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
const parseNum = (v?: string) => Number((v || '').replace(/\D/g, '')) || 0;

export const Chips = <T extends string>({ list, value, onChange, label }: { list: readonly T[]; value?: T; onChange: (v: T) => void; label: string }) => (
  <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
    {list.map((x) => (
      <button
        key={x}
        type="button"
        role="radio"
        aria-checked={value === x}
        onClick={() => onChange(x)}
        className={`rounded-full border px-3 py-1 text-[12.5px] font-medium transition-colors ${value === x
          ? 'border-[#3e91ab] bg-[#3e91ab] text-white'
          : 'border-gray-300 text-gray-700 hover:border-[#3e91ab] hover:text-[#3e91ab] dark:border-gray-600 dark:text-gray-200'}`}
      >
        {x}
      </button>
    ))}
  </div>
);

/** Nhãn cho nhóm nút (không dùng <label> để bấm vào chữ không chọn nhầm nút đầu) */
export const Lbl = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span className="text-[12px] font-medium text-gray-500 dark:text-gray-400">{label}</span>
    {children}
  </div>
);

const Step = ({ n, title, children, extra }: { n: number; title: string; children: React.ReactNode; extra?: React.ReactNode }) => (
  <section className="space-y-2.5 border-t border-gray-100 pt-3 first:border-0 first:pt-0 dark:border-gray-700">
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-gray-800 dark:text-gray-100">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#3e91ab]/15 text-[11px] font-bold text-[#3e91ab]">{n}</span>
        {title}
      </div>
      {extra}
    </div>
    {children}
  </section>
);

/* ---------------- Yêu cầu cọc ---------------- */

interface DepDraft { purpose: DepPurpose; amount: number; due: Dayjs }
const newDep = (): DepDraft => ({ purpose: 'Giữ lịch', amount: 1_000_000, due: dayjs().add(2, 'day').endOf('day') });

function DepositFields({ d, set, note }: { d: DepDraft; set: (p: Partial<DepDraft>) => void; note: string }) {
  return (
    <div className="space-y-2.5">
      <Lbl label="Mục đích cọc"><Chips list={DEP_PURPOSE} value={d.purpose} onChange={(purpose) => set({ purpose })} label="Mục đích cọc" /></Lbl>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Lbl label="Số tiền cọc *">
          <InputNumber<number>
            className="!w-full"
            min={0}
            step={100_000}
            value={d.amount}
            onChange={(v) => set({ amount: v ?? 0 })}
            formatter={fmtNum}
            parser={parseNum}
            addonAfter="đ"
          />
          <div className="mt-1 flex flex-wrap gap-1">
            {DEP_AMOUNTS.map((a) => (
              <button key={a} type="button" onClick={() => set({ amount: a })} className={`rounded border px-1.5 py-0.5 text-[11.5px] ${d.amount === a ? 'border-[#3e91ab] text-[#3e91ab]' : 'border-gray-200 text-gray-500 hover:text-[#3e91ab] dark:border-gray-600'}`}>
                {money(a)}
              </button>
            ))}
          </div>
        </Lbl>
        <Field label="Hạn chuyển khoản" required>
          <DatePicker className="!w-full" format="DD/MM/YYYY" value={d.due} allowClear={false} onChange={(v) => v && set({ due: v.endOf('day') })} disabledDate={(x) => x.isBefore(dayjs(), 'day')} />
        </Field>
      </div>
      <div className="rounded-md bg-gray-50 px-3 py-2 text-[12px] text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
        Nội dung chuyển khoản: <b className="text-gray-900 dark:text-gray-100">{note}</b> (tự gắn mã khách + mã báo giá) · Kế toán đối soát mới chuyển “Đã nhận cọc”.
      </div>
    </div>
  );
}

const buildDeposit = (d: DepDraft, quotes: Quote[], quoteCode: string, custCode: string): Deposit => {
  const day = dayjs().format('MMDD');
  const n = quotes.filter((q) => q.deposit?.code.startsWith(`CO-${day}-`)).length + 1;
  return { code: `CO-${day}-${String(n).padStart(2, '0')}`, amount: d.amount, purpose: d.purpose, due: d.due.valueOf(), status: 'waiting', note: depositNote(custCode, quoteCode) };
};

/* ---------------- Form tạo báo giá ---------------- */

type SubmitKind = 'draft' | 'send';
interface FormInit { base?: Quote; mode: 'new' | 'edit' | 'copy' }

function QuoteForm({ init, defDv, code, custCode, quotes, xung, name, onCancel, onSubmit }: {
  init: FormInit;
  defDv: DichVu;
  code: string;
  custCode: string;
  quotes: Quote[];
  xung: string;
  name: string;
  onCancel: () => void;
  onSubmit: (kind: SubmitKind, q: Omit<Quote, 'id' | 'code' | 'at' | 'by' | 'status'>, sendNow: boolean) => void;
}) {
  const b = init.base;
  const [dv, setDv] = useState<DichVu>(b?.dichVu ?? defDv);
  const [picked, setPicked] = useState<QuoteSvc[]>(b?.items ?? []);
  // Thẻ liệu trình (gói theo buổi, thẻ trả trước) báo cùng dịch vụ
  const pkgs = useCardPackages();
  const addCard = (id: string) => {
    const p = pkgs?.find((x) => x.id === id);
    if (p && !picked.some((x) => x.card?.code === p.card.code)) setPicked((ps) => [...ps, { name: p.name, price: p.price, card: p.card }]);
  };
  // Danh mục dịch vụ thật: gợi ý theo nhóm, gõ để tìm dịch vụ khác
  const [svcQ, setSvcQ] = useState('');
  const [opts, setOpts] = useState<SvcOpt[]>([]);
  const [svcLoading, setSvcLoading] = useState(false);
  useEffect(() => {
    let alive = true;
    setSvcLoading(true);
    const t = setTimeout(() => {
      searchServices(svcQ.trim() || DV_SEARCH[dv]).then((xs) => { if (alive) { setOpts(xs); setSvcLoading(false); } });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [svcQ, dv]);
  const [promo, setPromo] = useState<string | undefined>(b?.promo);
  const [discount, setDiscount] = useState<number>(b?.discount ?? 0);
  const [reason, setReason] = useState(b?.reason ?? '');
  const [channel, setChannel] = useState<SendCh>(b?.channel ?? 'Zalo');
  const [sendNow, setSendNow] = useState(true);
  const [sendAt, setSendAt] = useState<Dayjs>(dayjs().add(1, 'hour').startOf('hour'));
  const [valid, setValid] = useState<Dayjs>(() => {
    if (b && init.mode === 'edit') return dayjs(b.validUntil);
    const eom = dayjs().endOf('month');
    return eom.diff(dayjs(), 'day') < 7 ? dayjs().add(14, 'day').endOf('day') : eom;
  });
  const [depOn, setDepOn] = useState(!!b?.deposit);
  const [dep, setDep] = useState<DepDraft>(b?.deposit ? { purpose: b.deposit.purpose, amount: b.deposit.amount, due: dayjs(b.deposit.due) } : newDep);
  const [preview, setPreview] = useState(false);

  const items = picked;
  // Dịch vụ đã chọn luôn hiện (kể cả khi không nằm trong kết quả tìm), sau đó là gợi ý
  const svc: QuoteSvc[] = [...picked, ...opts.filter((o) => !picked.some((p) => p.name === o.name)).map((o) => ({ id: o.id > 0 ? o.id : undefined, name: o.name, price: o.price }))];
  // Danh mục có dịch vụ trùng tên khác mã: so theo mã, dịch vụ cũ không có mã thì so theo tên
  const same = (a: QuoteSvc, b: QuoteSvc) => (a.id != null && b.id != null ? a.id === b.id : a.name === b.name);
  const toggle = (x: QuoteSvc) => setPicked((ps) => (ps.some((p) => same(p, x)) ? ps.filter((p) => !same(p, x)) : [...ps, x]));
  const chk = checkQuote(items, discount, reason);
  const deposit = depOn ? buildDeposit(dep, quotes, code, custCode) : undefined;
  const draft = { dichVu: dv, items, promo, discount, reason: discount > 0 ? reason.trim() : undefined, channel, sendAt: sendNow ? Date.now() : sendAt.valueOf(), validUntil: valid.valueOf(), deposit, replaces: init.mode === 'copy' ? b?.id : b?.replaces };
  const depErr = depOn && dep.amount <= 0 ? 'Nhập số tiền cọc.' : undefined;
  const timeErr = !sendNow && sendAt.isBefore(dayjs()) ? 'Giờ hẹn gửi phải sau hiện tại.' : undefined;
  const sendLabel = chk.needApprove ? 'Gửi xin duyệt' : sendNow ? `Gửi qua ${channel}` : 'Hẹn gửi';

  // Đổi nhóm dịch vụ: bỏ dịch vụ đã chọn, giữ thẻ liệu trình
  const pickDv = (x: DichVu) => { if (x !== dv) { setDv(x); setPicked((ps) => ps.filter((p) => p.card)); } };

  return (
    <div className="space-y-3 rounded-xl border border-[#3e91ab]/40 bg-[#3e91ab]/[0.03] p-3.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[13.5px] font-bold text-gray-900 dark:text-gray-50">
          {init.mode === 'edit' ? 'Sửa báo giá nháp' : 'Báo giá tham khảo mới'} <span className="font-mono text-[#3e91ab]">{code}</span>
        </div>
        {init.mode === 'copy' && b && <span className="text-[11.5px] text-gray-500">Gửi bản này thì {b.code} chuyển “Đã thay thế”</span>}
      </div>

      <Step n={1} title="Chọn dịch vụ">
        <Chips list={DICH_VUS} value={dv} onChange={pickDv} label="Dịch vụ" />
        <Input.Search allowClear value={svcQ} onChange={(e) => setSvcQ(e.target.value)} loading={svcLoading} placeholder={`Tìm dịch vụ khác trong danh mục (đang gợi ý theo “${DV_SEARCH[dv]}”)`} />
        <div className="flex max-h-[260px] flex-col gap-1 overflow-y-auto">
          {svc.map((x) => {
            const on = picked.some((p) => same(p, x));
            return (
              <Checkbox
                key={x.id ?? x.name}
                checked={on}
                onChange={() => toggle(x)}
                className={`!m-0 !flex w-full items-center rounded-md border px-2.5 py-1.5 text-[13px] [&>span:last-child]:flex-1 ${on ? 'border-[#3e91ab] bg-white dark:bg-gray-800' : 'border-gray-200 dark:border-gray-700'}`}
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="min-w-0">
                    {x.card && <Tag color="purple" className="!mr-1.5 !text-[10px]">Thẻ liệu trình</Tag>}{x.name}
                    {x.card && <span className="block text-[11.5px] text-gray-500">{cardSummary(x.card)}</span>}
                  </span>
                  <span className="shrink-0 text-[12.5px] text-gray-600 dark:text-gray-300"><b>{money(x.price)}</b></span>
                </span>
              </Checkbox>
            );
          })}
          {!svc.length && !svcLoading && <span className="px-1 py-2 text-[12.5px] text-gray-400">Không tìm thấy dịch vụ — thử từ khoá khác.</span>}
        </div>
        <Select key={picked.length} showSearch value={null} placeholder={<span className="inline-flex items-center gap-1"><CreditCard size={14} />Thêm thẻ liệu trình (gói theo buổi, thẻ trả trước)</span>} onChange={addCard}
          optionFilterProp="label" loading={!pkgs} notFoundContent={pkgs ? cardEmptyText() : undefined} options={cardOptions(pkgs ?? [], true)} />
        <div className="text-[11.5px] text-gray-500">Giá lấy từ danh mục dịch vụ và gói thẻ (cùng giá màn Tạo lịch hẹn và POS) · không gõ giá tay · số buổi, mức độ bác sĩ xác định sau khám · thẻ liệu trình bán theo giá gói, không áp % giảm.</div>
      </Step>

      <Step n={2} title="Ưu đãi">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Chương trình đang chạy" full>
            <Select value={promo} onChange={setPromo} allowClear placeholder="— Không áp dụng —" options={PROMOS.map((x) => ({ value: x, label: x }))} />
          </Field>
          <Field label={`Giảm trong quyền (ngưỡng ${NGUONG_GIAM}%)`}>
            <InputNumber<number> className="!w-full" min={0} max={100} value={discount} onChange={(v) => setDiscount(v ?? 0)} addonAfter="%" />
          </Field>
          <Field label="Lý do giảm" required={discount > 0}>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} disabled={!discount} placeholder={discount ? 'VD: khách cũ giới thiệu' : 'Chỉ cần khi có giảm'} />
          </Field>
        </div>
        {chk.needApprove && <Alert type="warning" showIcon className="text-[12.5px]" message={`Giảm ${discount}% vượt quyền Tư vấn Online (${NGUONG_GIAM}%) → gửi Trưởng phòng duyệt trước khi gửi khách.`} />}
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-md bg-white px-3 py-2 text-[13px] dark:bg-gray-800">
          <span>Tổng giá từ: <b>{money(chk.total)}</b></span>
          {discount > 0 && chk.total != null && <span>Giảm {discount}%: <b className="text-red-500">−{money(chk.total - (chk.after ?? 0))}</b></span>}
          <span>Còn từ: <b className="text-[15px] text-[#3e91ab]">{money(chk.after)}</b></span>
          <span className="text-[11.5px] text-gray-400">trần {chk.cap}% {items.filter((x) => !x.card).length > 1 ? 'mỗi combo' : 'mỗi dịch vụ'}{items.some((x) => x.card) ? ' · thẻ liệu trình giữ giá gói' : ''}</span>
        </div>
      </Step>

      <Step n={3} title="Gửi khách">
        <Lbl label="Kênh gửi"><Chips list={SEND_CH} value={channel} onChange={setChannel} label="Kênh gửi" /></Lbl>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Lbl label="Ngày gửi">
            <Segmented block size="middle" value={sendNow ? 'now' : 'later'} onChange={(v) => setSendNow(v === 'now')} options={[{ value: 'now', label: 'Gửi ngay' }, { value: 'later', label: 'Hẹn ngày giờ' }]} />
            {!sendNow && (
              <DatePicker showTime={{ format: 'HH:mm', minuteStep: 15 }} format="HH:mm DD/MM/YYYY" className="!mt-1.5 !w-full" value={sendAt} allowClear={false} onChange={(v) => v && setSendAt(v)} disabledDate={(x) => x.isBefore(dayjs(), 'day')} />
            )}
          </Lbl>
          <Field label="Hạn giữ ưu đãi">
            <DatePicker className="!w-full" format="DD/MM/YYYY" value={valid} allowClear={false} onChange={(v) => v && setValid(v.endOf('day'))} disabledDate={(x) => x.isBefore(dayjs(), 'day')} />
          </Field>
        </div>
      </Step>

      <Step n={4} title="Yêu cầu cọc" extra={<Switch size="small" checked={depOn} onChange={setDepOn} aria-label="Yêu cầu cọc" />}>
        {depOn
          ? <DepositFields d={dep} set={(p) => setDep((x) => ({ ...x, ...p }))} note={depositNote(custCode, code)} />
          : <div className="text-[12px] text-gray-500">Không kèm cọc. Bật để gửi kèm mã QR / nội dung chuyển khoản theo chính sách cọc.</div>}
      </Step>

      <div>
        <button type="button" onClick={() => setPreview((x) => !x)} className="text-[12.5px] font-medium text-[#3e91ab] hover:underline">
          {preview ? 'Ẩn tin gửi khách' : 'Xem tin gửi khách'}
        </button>
        {preview && (
          <pre className="mt-1.5 whitespace-pre-wrap rounded-md border border-gray-200 bg-white p-2.5 font-sans text-[12.5px] text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
            {items.length ? quoteMessage({ ...draft, code }, xung, name) : 'Chọn dịch vụ để xem tin.'}
          </pre>
        )}
      </div>

      {(chk.error || depErr || timeErr) && picked.length > 0 && <Alert type="error" showIcon className="text-[12.5px]" message={chk.error || depErr || timeErr} />}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button onClick={onCancel}>Hủy</Button>
        <Button disabled={!items.length} onClick={() => onSubmit('draft', draft, sendNow)}>Lưu nháp</Button>
        <Button type="primary" icon={<Send size={14} />} disabled={!!(chk.error || depErr || timeErr)} onClick={() => onSubmit('send', draft, sendNow)} className="!bg-[#3e91ab] disabled:!bg-gray-200">
          {sendLabel}
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Bảng lịch sử báo giá + bảng cọc ---------------- */

type CardAct = 'edit' | 'copy' | 'approve' | 'reject' | 'send' | 'replied' | 'receipt' | 'depConfirm' | 'depReject' | 'cancel' | 'deposit' | 'book';
type Act = (a: CardAct, extra?: string | DepDraft) => void;

const isClosed = (q: Quote) => q.status === 'replaced' || q.status === 'cancelled';
const isSent = (q: Quote) => q.status === 'sent' || q.status === 'replied';

/** Bảng đầu xanh như mẫu phiếu Telesales */
const TABLE = [
  '[&_.ant-table-thead>tr>th]:!bg-[#3e91ab] [&_.ant-table-thead>tr>th]:!py-2 [&_.ant-table-thead>tr>th]:!text-[12px] [&_.ant-table-thead>tr>th]:!font-semibold [&_.ant-table-thead>tr>th]:!text-white',
  '[&_.ant-table-thead>tr>th::before]:!hidden [&_.ant-table-tbody>tr>td]:!py-2 [&_.ant-table-tbody>tr>td]:!align-top [&_.ant-table-tbody>tr>td]:!text-[12.5px]',
  '[&_.ant-table-tbody>tr:nth-child(even)>td]:!bg-[#3e91ab]/[0.05]',
].join(' ');

/** Cột Trạng thái: nhãn + hạn ưu đãi / lý do / bản thay thế */
function QuoteStatus({ q, now }: { q: Quote; now: number }) {
  const left = daysLeft(q, now);
  const st = QUOTE_ST[q.status];
  if (isSent(q)) {
    if (left < 0) return <span className="text-gray-500">{st.label} · hết hạn {dayjs(q.validUntil).format('DD/MM')}</span>;
    return <span className={left <= 3 ? 'font-medium text-red-500' : 'text-gray-800 dark:text-gray-100'}>{st.label} · hạn {dayjs(q.validUntil).format('DD/MM')}{left <= 3 ? ` (còn ${left} ngày)` : ''}</span>;
  }
  if (q.status === 'replaced') return <span className="text-gray-500">{st.label}{q.replacedBy ? ` (bằng ${q.replacedBy})` : ''}</span>;
  if (q.status === 'cancelled') return <span className="text-gray-500">{st.label}{q.cancelReason ? ` (${q.cancelReason})` : ''}</span>;
  if (q.status === 'scheduled') {
    return q.sendAt <= now
      ? <span className="font-medium text-red-500">Đến giờ gửi · {dayjs(q.sendAt).format('HH:mm DD/MM')}</span>
      : <span className="text-purple-600 dark:text-purple-400">Hẹn gửi {dayjs(q.sendAt).format('HH:mm DD/MM')}</span>;
  }
  if (q.status === 'pending') return <span className="font-medium text-orange-600">Chờ duyệt (giảm {q.discount}%)</span>;
  return <span className="text-gray-600 dark:text-gray-300">{st.label}</span>;
}

/** Trạng thái cọc: báo giá chưa gửi thì "gửi kèm", báo giá đã thay / huỷ khi khách chưa chuyển thì hết hiệu lực */
const depTag = (q: Quote) => {
  const d = q.deposit!;
  if (isClosed(q) && d.status === 'waiting') return <Tag className="!m-0">Hết hiệu lực</Tag>;
  if (!isSent(q) && !isClosed(q)) return <Tag className="!m-0">Gửi kèm báo giá</Tag>;
  return <Tag color={DEP_ST[d.status].color} className="!m-0">{DEP_ST[d.status].label}</Tag>;
};

/** Dòng phụ dưới trạng thái cọc: lý do trả lại, chờ / đã đẩy qua POS, lý do hoàn / bỏ cọc */
export function DepositNote({ d }: { d: Deposit }) {
  const cls = 'text-[11.5px] leading-snug text-gray-500';
  const o = d.order;
  if (d.status === 'waiting' && d.rejectNote) return <span className={`${cls} !text-red-500`}>Kế toán: {d.rejectNote}</span>;
  if (d.status === 'checking') return <span className={cls}>Chờ Kế toán đối soát</span>;
  if (d.status === 'refund_pending') return <span className={cls}>Báo giá đã huỷ, tiền cọc chờ hoàn cho khách</span>;
  if (d.status === 'cancelled') return <span className={cls}>Báo giá đã thay thế hoặc huỷ, khoản này không còn hiệu lực</span>;
  return (
    <>
      {d.status === 'received' && !o && <span className={cls}>Chờ Kế toán đẩy qua POS</span>}
      {o?.status === 'deposit' && <span className={cls}>Đã đẩy POS · đơn <b className="font-mono text-gray-700 dark:text-gray-200">{o.code}</b> · chờ POS thu cọc</span>}
      {o?.status === 'deposit_paid' && <span className={cls}>POS đã thu cọc · đơn <b className="font-mono text-gray-700 dark:text-gray-200">{o.posCode}</b> · còn nợ <b className="text-[#3e91ab]">{money(orderDue(o))}</b></span>}
      {o?.status === 'paid' && <span className={cls}>Đơn <b className="font-mono text-gray-700 dark:text-gray-200">{o.posCode ?? o.code}</b> · POS đã thu đủ {money(o.finalAmount)}</span>}
      {o?.status === 'cancelled' && <span className={cls}>Đơn {o.code} đã huỷ</span>}
      {d.closeReason && <span className={cls}>{d.closeReason}</span>}
    </>
  );
}

const quoteColumns = (now: number): ColumnsType<Quote> => [
  { title: 'Ngày', key: 'at', width: 50, render: (_, q) => <span title={dayjs(q.at).format('HH:mm DD/MM/YYYY')}>{dayjs(q.at).format('DD/MM')}</span> },
  { title: 'Mã', key: 'code', width: 92, render: (_, q) => <span className="whitespace-nowrap font-mono text-[12px] font-semibold">{q.code}</span> },
  {
    title: 'Gói / dịch vụ', key: 'items', width: 170,
    render: (_, q) => <>{q.items.map((x, i) => <div key={`${x.id ?? x.name}-${i}`}>{x.name}{x.card && <Tag color="purple" className="!ml-1 !text-[10px]">Thẻ</Tag>}</div>)}<div className="text-[11.5px] text-gray-400">{q.dichVu}</div></>,
  },
  {
    title: 'Giá', key: 'price', width: 108,
    render: (_, q) => {
      const c = checkQuote(q.items, q.discount, q.reason);
      return (
        <>
          <div className="text-[11.5px] text-gray-500">Giá từ</div>
          {q.discount > 0
            ? <><s className="text-gray-400">{money(c.total)}</s><div>→ <b className="text-[#3e91ab]">{money(c.after)}</b></div></>
            : <div>{money(c.total)}</div>}
        </>
      );
    },
  },
  {
    title: 'Mức trợ giá', key: 'discount', width: 108,
    render: (_, q) => (
      <>
        {q.discount > 0 ? <div>Giảm {q.discount}%{q.reason && <div className="text-[11.5px] text-gray-500">{q.reason}</div>}</div> : <div>Không trợ giá</div>}
        {q.promo && <div className="text-[11.5px] text-emerald-600 dark:text-emerald-400">+ {q.promo}</div>}
      </>
    ),
  },
  { title: 'Người báo', dataIndex: 'by', width: 84 },
  { title: 'Trạng thái', key: 'status', width: 130, render: (_, q) => <QuoteStatus q={q} now={now} /> },
];

/** Dòng mở rộng: kênh gửi + các thao tác của báo giá */
function QuoteDetail({ q, custCode, readOnly, canApprove, act }: { q: Quote; custCode: string; readOnly?: boolean; canApprove?: boolean; act: Act }) {
  const [cancelWhy, setCancelWhy] = useState('');
  const [depOpen, setDepOpen] = useState(false);
  const [dep, setDep] = useState<DepDraft>(newDep);
  const live = isSent(q);
  const closed = isClosed(q);

  return (
    <div className="space-y-2 py-1 text-[12.5px]">
      <div className="text-gray-600 dark:text-gray-300">
        {q.status === 'scheduled' ? 'Hẹn gửi' : live || closed ? 'Gửi' : 'Sẽ gửi'} qua <b>{q.channel}</b>
        {(q.status === 'scheduled' || live || q.status === 'replaced') && <> · {dayjs(q.sendAt).format('HH:mm DD/MM/YYYY')}</>}
        {' '}· hạn ưu đãi {dayjs(q.validUntil).format('DD/MM/YYYY')} · tạo {dayjs(q.at).format('HH:mm DD/MM')}
        {q.deposit && <> · cọc <b>{q.deposit.code}</b> {money(q.deposit.amount)}</>}
      </div>

      {!readOnly && !closed && (
        <div className="flex flex-wrap items-center gap-1.5">
          {q.status === 'draft' && <Button size="small" onClick={() => act('edit')}>Sửa nháp</Button>}
          {q.status === 'pending' && (canApprove
            ? <>
                <Button size="small" type="primary" className="!bg-[#3e91ab]" onClick={() => act('approve')}>Duyệt giảm {q.discount}%</Button>
                <Button size="small" onClick={() => act('reject')}>Trả về nháp</Button>
              </>
            : <span className="font-medium text-orange-600">Chờ Trưởng phòng duyệt — chưa gửi khách được</span>)}
          {(q.status === 'approved' || q.status === 'scheduled') && <Button size="small" type="primary" icon={<Send size={13} />} className="!bg-[#3e91ab]" onClick={() => act('send')}>Gửi ngay qua {q.channel}</Button>}
          {q.status === 'sent' && <Button size="small" onClick={() => act('replied')}>Khách đã phản hồi</Button>}
          {live && <Button size="small" type="primary" icon={<CalendarPlus size={13} />} className="!bg-[#3e91ab]" onClick={() => act('book')}>Đặt lịch theo báo giá</Button>}
          {live && (
            // Mỗi báo giá một khoản cọc (PA, P9): đang đối soát hoặc đã nhận tiền thì chưa thay thế được
            <Tooltip title={q.deposit?.status === 'checking' ? 'Khoản cọc đang chờ đối soát. Kế toán xác nhận hoặc trả lại trước.' : q.deposit?.status === 'received' ? 'Báo giá đã nhận cọc. Phải huỷ bản này trước, tiền cọc chuyển sang chờ hoàn.' : undefined}>
              <Button size="small" icon={<FilePlus2 size={13} />} disabled={q.deposit?.status === 'checking' || q.deposit?.status === 'received'} onClick={() => act('copy')}>Tạo bản mới</Button>
            </Tooltip>
          )}
          {live && !q.deposit && <Button size="small" icon={<Banknote size={13} />} onClick={() => setDepOpen((x) => !x)}>Yêu cầu cọc</Button>}
          <Popconfirm
            placement="topRight"
            title={`Huỷ báo giá ${q.code}?`}
            description={(
              <div className="w-60">
                {q.deposit?.status === 'received' && <div className="mb-1 text-[12px] text-red-500">Báo giá đã nhận cọc. Huỷ thì khoản {q.deposit.code} chuyển sang Chờ hoàn cọc, không dùng được cho báo giá khác.</div>}
                <Input size="small" autoFocus value={cancelWhy} onChange={(e) => setCancelWhy(e.target.value)} placeholder="Lý do huỷ (bắt buộc)" className="!mt-1 !w-60" />
              </div>
            )}
            okText="Huỷ báo giá"
            cancelText="Không"
            okButtonProps={{ danger: true, disabled: !cancelWhy.trim() }}
            onConfirm={() => { act('cancel', cancelWhy.trim()); setCancelWhy(''); }}
          >
            <Tooltip title={q.deposit?.status === 'checking' ? 'Khoản cọc đang chờ đối soát. Kế toán xác nhận hoặc trả lại trước.' : q.deposit?.order ? 'Cọc đã đẩy qua POS. Kế toán thu hồi lệnh hoặc xử lý hoàn bên POS trước.' : undefined}>
              <Button size="small" type="text" danger className="!ml-auto" disabled={q.deposit?.status === 'checking' || !!q.deposit?.order}>Huỷ báo giá</Button>
            </Tooltip>
          </Popconfirm>
        </div>
      )}

      {depOpen && (
        <div className="space-y-2.5 rounded-lg border border-amber-200 bg-amber-50/40 p-3 dark:border-amber-500/30 dark:bg-amber-500/5">
          <DepositFields d={dep} set={(p) => setDep((x) => ({ ...x, ...p }))} note={depositNote(custCode, q.code)} />
          <div className="flex justify-end gap-2">
            <Button size="small" onClick={() => setDepOpen(false)}>Hủy</Button>
            <Button size="small" type="primary" className="!bg-[#3e91ab]" disabled={dep.amount <= 0} onClick={() => { act('deposit', dep); setDepOpen(false); }}>Gửi yêu cầu cọc qua {q.channel}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Nút có ô nhập trong Popconfirm (lý do / mã sao kê) */
export function AskButton({ label, title, placeholder, required, danger, primary, onOk }: {
  label: string; title: string; placeholder: string; required?: boolean; danger?: boolean; primary?: boolean; onOk: (text: string) => void;
}) {
  const [text, setText] = useState('');
  return (
    <Popconfirm
      title={title}
      description={<Input size="small" value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} className="!mt-1 !w-64" />}
      okText="Xác nhận"
      cancelText="Không"
      okButtonProps={{ danger, disabled: required && !text.trim() }}
      onConfirm={() => { onOk(text.trim()); setText(''); }}
    >
      <Button size="small" danger={danger} type={primary ? 'primary' : 'default'} className={primary ? '!bg-[#3e91ab]' : ''}>{label}</Button>
    </Popconfirm>
  );
}

const depositColumns = (readOnly: boolean | undefined, canConfirm: boolean, act: (q: Quote) => Act): ColumnsType<Quote> => [
  {
    title: 'Mã cọc', key: 'code', width: 104,
    render: (_, q) => <><div className="whitespace-nowrap font-mono text-[12px] font-semibold">{q.deposit!.code}</div><div className="whitespace-nowrap text-[11.5px] text-gray-500">theo {q.code}</div></>,
  },
  {
    title: 'Ngày', key: 'at', width: 76,
    render: (_, q) => <>{dayjs(isSent(q) ? q.sendAt : q.at).format('DD/MM')}<div className="text-[11.5px] text-gray-500">hạn {dayjs(q.deposit!.due).format('DD/MM')}</div></>,
  },
  { title: 'Số tiền', key: 'amount', width: 104, render: (_, q) => <b>{money(q.deposit!.amount)}</b> },
  { title: 'Mục đích', key: 'purpose', width: 84, render: (_, q) => q.deposit!.purpose },
  { title: 'Nội dung CK', key: 'note', width: 132, render: (_, q) => <span className="font-mono text-[12px]">{q.deposit!.note}</span> },
  {
    title: 'Trạng thái', key: 'status', width: 200,
    render: (_, q) => (
      <div className="flex flex-col items-start gap-1">
        {depTag(q)}
        <DepositNote d={q.deposit!} />
        {!readOnly && isSent(q) && q.deposit!.status === 'waiting' && <Button size="small" onClick={() => act(q)('receipt')}>Khách đã gửi biên lai</Button>}
        {/* Quyền do server báo (can_confirm), FE không tự suy */}
        {canConfirm && q.deposit!.serverId && (q.deposit!.status === 'checking' || (q.deposit!.status === 'waiting' && !isClosed(q))) && (
          <AskButton primary label="Xác nhận đã nhận cọc" title={`Xác nhận đã nhận ${money(q.deposit!.amount)} — ${q.deposit!.note}?`} placeholder="Mã giao dịch trên sao kê (không bắt buộc)" onOk={(t) => act(q)('depConfirm', t)} />
        )}
        {canConfirm && q.deposit!.serverId && q.deposit!.status === 'checking' && (
          <AskButton danger label="Chưa khớp" title="Sao kê chưa có khoản này?" placeholder="Lý do (bắt buộc), VD: chưa thấy tiền về" required onOk={(t) => act(q)('depReject', t)} />
        )}
      </div>
    ),
  },
];

/* ---------------- Tab ---------------- */

type Filter = 'all' | 'live' | 'todo' | 'closed';
const TODO: Quote['status'][] = ['draft', 'pending', 'approved', 'scheduled'];

export default function QuoteTab({ r, l, by, xung, now, mang, readOnly, canApprove, openSignal = 0, onBook }: {
  r: ChannelCustomerItem;
  l: LeadLocal;
  by: string;
  xung: string;
  now: number;
  mang?: Mang;
  /** Trực page / Sale cơ sở: chỉ xem lịch sử */
  readOnly?: boolean;
  /** Người vận hành (Trưởng phòng) duyệt giảm vượt ngưỡng */
  canApprove?: boolean;
  /** Sang tab Lịch hẹn, mở form tạo lịch đã điền dịch vụ của báo giá này */
  onBook?: (quoteId: string) => void;
  /** Tăng lên mỗi lần bấm bước "Báo giá & cọc" khi chưa có báo giá → mở form tạo */
  openSignal?: number;
}) {
  const { patch, addTouch, addMsg, depositAct } = useLeadStore();
  const quotes = useMemo(() => l.quotes ?? [], [l.quotes]);
  // Mã báo giá / mã cọc đánh số chung mọi khách để Kế toán, POS tra không nhầm
  const byId = useLeadStore((s) => s.byId);
  const everyQuote = useMemo(() => Object.values(byId).flatMap((x) => x.quotes ?? []), [byId]);
  const [form, setForm] = useState<FormInit | null>(openSignal && !readOnly ? { mode: 'new' } : null);
  useEffect(() => { if (openSignal && !readOnly) setForm((f) => f ?? { mode: 'new' }); }, [openSignal, readOnly]);
  const [filter, setFilter] = useState<Filter>('all');
  const name = (r.display_name || r.fullname || 'Khách').trim();
  const custCode = r.customer_code || `KH${r.id}`;
  const defDv = dichVuOf(mang, l.needs) ?? 'Cấy tóc';

  const pass: Record<Filter, (q: Quote) => boolean> = {
    all: () => true,
    live: (q) => isLive(q, now),
    todo: (q) => TODO.includes(q.status),
    closed: (q) => q.status === 'replaced' || q.status === 'cancelled' || ((q.status === 'sent' || q.status === 'replied') && q.validUntil < now),
  };
  const shown = quotes.filter(pass[filter]);
  // Khoản cọc trên server mà máy này chưa có báo giá: chỉ hiện ở bảng Cọc
  const [orphans, setOrphans] = useState<Quote[]>([]);
  const [canConfirm, setCanConfirm] = useState(false);
  const [canApproveApi, setCanApproveApi] = useState(false);
  const serverLead = !isDemoId(r.id);
  // Quyền duyệt do server báo (can_approve); khách mẫu vẫn theo vai trò đang xem
  const approver = serverLead ? canApproveApi : canApprove;
  const deposits = [...quotes.filter((q) => q.deposit), ...orphans];
  const [openRow, setOpenRow] = useState<string | null>(null);
  const count = (f: Filter) => quotes.filter(pass[f]).length;

  const save = (next: Quote[]) => patch(r.id, { quotes: next });
  const current = useCallback(() => useLeadStore.getState().byId[r.id]?.quotes ?? [], [r.id]);
  const custRef = useMemo(() => ({ id: r.id, name, code: custCode, phone: r.phone }), [r.id, name, custCode, r.phone]);
  /** Tải báo giá, cọc trên server, gộp vào bản trên máy */
  const syncDeposits = useCallback(async () => {
    if (isDemoId(r.id)) return;
    const res = await reloadQuotes(r.id, custRef);
    if (res.error) message.warning(`Chưa tải được báo giá, cọc từ máy chủ: ${res.error}`);
    setCanConfirm(res.canConfirm);
    setCanApproveApi(res.canApprove);
    setOrphans(res.orphans);
  }, [r.id, custRef]);
  useEffect(() => { setOrphans([]); setCanConfirm(false); setCanApproveApi(false); syncDeposits(); }, [syncDeposits]);
  /** Sau mỗi thao tác báo giá: server đã đổi báo giá, cọc và tự ghi lịch sử chạm */
  const refresh = async () => { await syncDeposits(); reloadTouches(r.id); };
  /** "Gửi" chỉ ghi nhận trên hệ thống; tin vẫn hiện ở tab Nhắn tin để nhân viên chép gửi khách */
  const announceSent = (serverId: number) => {
    const q = current().find((x) => x.serverId === serverId);
    if (!q) return;
    if (CHAT_CH.includes(q.channel)) addMsg(r.id, { from: 'n', text: quoteMessage(q, xung, name), by });
    message.success(`Đã ghi nhận gửi ${q.code} qua ${q.channel}. Hệ thống chưa tự gửi tin, nhân viên gửi cho khách.`, 6);
  };
  const submitServer = async (kind: SubmitKind, d: Omit<Quote, 'id' | 'code' | 'at' | 'by' | 'status'>, sendNow: boolean) => {
    const fmt = (t: number) => dayjs(t).format('YYYY-MM-DD HH:mm:ss');
    const editing = form?.mode === 'edit' ? form.base : undefined;
    const old = form?.mode === 'copy' ? form.base : undefined;
    const body: QuoteBody = {
      action: kind === 'draft' ? 'draft' : 'submit',
      dich_vu: d.dichVu,
      items: d.items.map((x) => ({ id: x.id ?? null, name: x.name, price: x.price, card: x.card ?? null })),
      promo: d.promo ?? null,
      discount: d.discount,
      reason: d.reason ?? null,
      channel: d.channel,
      send_at: sendNow ? null : fmt(d.sendAt),
      valid_until: fmt(d.validUntil),
      replaces_id: old?.serverId ?? editing?.replacesId ?? null,
      deposit: d.deposit ? { amount: d.deposit.amount, purpose: d.deposit.purpose, due_at: fmt(d.deposit.due) } : null,
    };
    const res = editing?.serverId ? await updateQuote(editing.serverId, body) : await createQuote(r.id, body);
    // Lỗi thì giữ form để sửa rồi gửi lại
    if (!res.ok || !res.data) { message.error(res.message || 'Chưa lưu được báo giá'); return; }
    // Nháp chỉ có trên máy: bản trên server thay luôn. Bản cũ chỉ có trên máy: ghi liên kết, khi bản mới được gửi mới thành "Đã thay thế"
    const localDraft = editing && !editing.serverId ? editing.id : undefined;
    const localOld = old && !old.serverId ? old.id : localDraft ? editing!.replaces : undefined;
    if (localDraft || localOld) {
      patch(r.id, { quotes: current().filter((x) => x.id !== localDraft).map((x) => (x.id === localOld && !x.serverId ? { ...x, nextServerId: res.data!.id } : x)) });
    }
    setForm(null);
    await refresh();
    const st = res.data.status;
    if (st === 'sent') announceSent(res.data.id);
    else message.success(st === 'pending' ? `Đã gửi xin duyệt ${res.data.code}` : st === 'scheduled' ? `Đã hẹn gửi ${res.data.code}, đến giờ nhân viên bấm gửi` : `Đã lưu nháp ${res.data.code}`);
  };
  /** Đưa cọc lên server, dùng mã server cấp; lỗi thì báo và trả null để không gửi khách */
  const pushDeposit = async (q: Quote, d: Deposit): Promise<Deposit | null> => {
    if (isDemoId(r.id) || d.serverId) return d;
    const res = await createDeposit(r.id, { quote_code: q.code, amount: d.amount, purpose: d.purpose, due_at: dayjs(d.due).format('YYYY-MM-DD HH:mm:ss'), note: d.note });
    if (!res.ok || !res.data) { message.error(res.message || 'Không tạo được yêu cầu cọc'); return null; }
    return depositFromServer(res.data, d);
  };
  /** Gắn khách + người tạo vào khoản cọc để Kế toán / POS tra được */
  const stamp = (d: Deposit): Deposit => ({ ...d, cust: { id: r.id, name, code: custCode, phone: r.phone }, by, createdAt: d.createdAt ?? Date.now() });
  /** Gửi tới khách: tin vào tab Nhắn tin (kênh chat) + ghi lịch sử chạm */
  const deliver = async (q0: Quote, list0: Quote[], at = Date.now()) => {
    const deposit = q0.deposit && await pushDeposit(q0, q0.deposit);
    if (q0.deposit && !deposit) return false;
    const q: Quote = deposit ? { ...q0, deposit } : q0;
    const list = list0.map((x) => (x.id === q.id ? q : x));
    const next = markSent(list, q.id, at);
    save(next);
    const sent = next.find((x) => x.id === q.id)!;
    if (CHAT_CH.includes(q.channel)) addMsg(r.id, { from: 'n', text: quoteMessage(sent, xung, name), by });
    addTouch(r.id, { kind: 'msg', text: `${q.channel} · gửi báo giá ${q.code} · còn từ ${money(checkQuote(q.items, q.discount, q.reason).after)}${q.deposit ? ` · kèm yêu cầu cọc ${money(q.deposit.amount)}` : ''}`, by });
    message.success(`Đã gửi báo giá ${q.code} qua ${q.channel}`);
    return true;
  };

  const submit = async (kind: SubmitKind, d: Omit<Quote, 'id' | 'code' | 'at' | 'by' | 'status'>, sendNow: boolean) => {
    if (serverLead) { await submitServer(kind, d, sendNow); return; }
    const editing = form?.mode === 'edit' ? form.base : undefined;
    const code = editing?.code ?? nextQuoteCode(everyQuote, Date.now());
    const chk = checkQuote(d.items, d.discount, d.reason);
    const status: Quote['status'] = kind === 'draft' ? 'draft' : chk.needApprove ? 'pending' : sendNow ? 'sent' : 'scheduled';
    const q: Quote = { ...d, deposit: d.deposit && stamp(d.deposit), id: editing?.id ?? uid(), code, at: editing?.at ?? Date.now(), by, status };
    const list = editing ? quotes.map((x) => (x.id === q.id ? q : x)) : [q, ...quotes];
    // Lỗi tạo cọc thì giữ form để gửi lại
    if (status === 'sent') { if (await deliver(q, list, d.sendAt)) setForm(null); return; }
    setForm(null);
    save(list);
    const log = {
      draft: `Lưu nháp báo giá ${code}`,
      pending: `Xin duyệt báo giá ${code} · giảm ${d.discount}% (vượt ngưỡng ${NGUONG_GIAM}%)`,
      scheduled: `Hẹn gửi báo giá ${code} qua ${d.channel} lúc ${dayjs(d.sendAt).format('HH:mm DD/MM')}`,
    }[status as 'draft' | 'pending' | 'scheduled'];
    addTouch(r.id, { kind: 'note', text: log, by });
    message.success(status === 'pending' ? 'Đã gửi xin duyệt — báo giá ở trạng thái Chờ duyệt' : log);
  };

  const act = (q: Quote): Act => async (a, extra) => {
    const upd = (p: Partial<Quote>, log: string, kind: 'note' | 'msg' = 'note') => {
      save(quotes.map((x) => (x.id === q.id ? { ...x, ...p } : x)));
      addTouch(r.id, { kind, text: log, by });
    };
    if (a === 'edit' || a === 'copy') { setForm({ mode: a, base: q }); return; }
    if (a === 'book') { onBook?.(q.id); return; }
    if (q.serverId && (a === 'approve' || a === 'reject' || a === 'send' || a === 'replied' || a === 'cancel')) {
      const res = await quoteAct(q.serverId, a, a === 'cancel' ? (extra as string) : undefined);
      if (!res.ok || !res.data) { message.error(res.message || 'Chưa cập nhật được báo giá'); return; }
      // Huỷ báo giá đã nhận cọc: khoản chuyển chờ hoàn nên nhãn Cọc mất, trừ khi còn báo giá khác đã nhận cọc
      if (a === 'cancel' && q.deposit?.status === 'received' && !current().some((x) => x.id !== q.id && x.deposit?.status === 'received')) emitCustomerPatched(r.id, { has_received_deposit: false });
      await refresh();
      if (res.data.status === 'sent' && q.status !== 'sent') announceSent(res.data.id);
      else message.success({ approve: 'Đã duyệt báo giá', reject: 'Đã trả về nháp', send: 'Đã ghi nhận gửi', replied: 'Đã ghi nhận khách phản hồi', cancel: 'Đã huỷ báo giá' }[a]);
      return;
    }
    if (a === 'approve') {
      // Duyệt xong: gửi luôn nếu không hẹn giờ
      if (q.sendAt <= Date.now()) { addTouch(r.id, { kind: 'note', text: `Duyệt giảm ${q.discount}% cho ${q.code}`, by }); deliver(q, quotes); return; }
      upd({ status: 'scheduled' }, `Duyệt giảm ${q.discount}% cho ${q.code} · chờ đến giờ hẹn gửi`);
      return;
    }
    if (a === 'reject') { upd({ status: 'draft' }, `Trả ${q.code} về nháp — chưa duyệt mức giảm ${q.discount}%`); return; }
    if (a === 'send') { deliver(q, quotes); return; }
    if (a === 'replied') { upd({ status: 'replied' }, `Khách phản hồi báo giá ${q.code}`); return; }
    const sid = q.deposit?.serverId;
    if (a === 'receipt') {
      const err = sid ? await runDepositApi(r.id, sid, { type: 'receipt' }, by) : depositAct(r.id, q.id, { type: 'receipt' }, by);
      if (err) message.error(err); else message.success('Đã chuyển Kế toán đối soát');
      if (sid) syncDeposits();
      return;
    }
    if ((a === 'depConfirm' || a === 'depReject') && sid) {
      const text = typeof extra === 'string' ? extra : '';
      const err = await runDepositApi(r.id, sid, a === 'depConfirm' ? { type: 'confirm', bankRef: text } : { type: 'reject', note: text }, by);
      if (err) message.error(err); else message.success(a === 'depConfirm' ? `Đã xác nhận nhận cọc ${q.deposit!.code}` : 'Đã trả về Chờ chuyển khoản');
      syncDeposits();
      return;
    }
    if (a === 'cancel') { upd({ status: 'cancelled', cancelReason: extra as string }, `Huỷ báo giá ${q.code}: ${extra}`); return; }
    if (a === 'deposit' && extra && typeof extra !== 'string') {
      const deposit = await pushDeposit(q, stamp(buildDeposit(extra, everyQuote, q.code, custCode)));
      if (!deposit) return;
      save(current().map((x) => (x.id === q.id ? { ...x, deposit } : x)));
      if (CHAT_CH.includes(q.channel)) {
        addMsg(r.id, { from: 'n', text: `Dạ em gửi ${xung} thông tin cọc ${money(deposit.amount)} để ${deposit.purpose.toLowerCase()} (báo giá ${q.code}), hạn ${dayjs(deposit.due).format('DD/MM')}. Nội dung chuyển khoản: ${deposit.note}. Chuyển xong ${xung} gửi em ảnh biên lai nhé.`, by });
      }
      addTouch(r.id, { kind: 'msg', text: `${q.channel} · yêu cầu cọc ${deposit.code} · ${money(deposit.amount)} · ${deposit.purpose}`, by });
      message.success(`Đã gửi yêu cầu cọc ${deposit.code}`);
    }
  };

  // Khách thật: server cấp mã khi lưu
  const formCode = form?.mode === 'edit' && form.base ? form.base.code : serverLead ? 'BG-(cấp khi lưu)' : nextQuoteCode(everyQuote, now);

  return (
    <div className="space-y-3">
      {form && (
        <QuoteForm
          key={`${form.mode}-${form.base?.id ?? 'new'}`}
          init={form}
          defDv={defDv}
          code={formCode}
          custCode={custCode}
          quotes={everyQuote}
          xung={xung}
          name={name}
          onCancel={() => setForm(null)}
          onSubmit={submit}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">Lịch sử báo giá{quotes.length ? ` (${quotes.length})` : ''}</div>
        {!readOnly && !form && (
          <Button type="primary" size="small" icon={<FilePlus2 size={14} />} className="!bg-[#3e91ab]" onClick={() => setForm({ mode: 'new' })}>Tạo báo giá</Button>
        )}
      </div>

      {quotes.length > 0 && (
        <Segmented
          size="small"
          value={filter}
          onChange={(v) => setFilter(v as Filter)}
          options={[
            { value: 'all', label: `Tất cả ${quotes.length}` },
            { value: 'live', label: `Còn hiệu lực ${count('live')}` },
            { value: 'todo', label: `Chưa gửi ${count('todo')}` },
            { value: 'closed', label: `Hết hạn · đã đóng ${count('closed')}` },
          ]}
          className="max-w-full overflow-x-auto"
        />
      )}

      <Table<Quote>
        rowKey="id"
        size="small"
        bordered={false}
        pagination={false}
        dataSource={shown}
        columns={quoteColumns(now)}
        scroll={{ x: 742 }}
        className={TABLE}
        rowClassName={(q) => `cursor-pointer ${isClosed(q) ? 'text-gray-500' : ''}`}
        expandable={{
          expandRowByClick: true,
          showExpandColumn: false,
          expandedRowKeys: openRow ? [openRow] : [],
          onExpand: (open, q) => setOpenRow(open ? q.id : null),
          expandedRowRender: (q) => <QuoteDetail q={q} custCode={custCode} readOnly={readOnly} canApprove={q.serverId ? canApproveApi : approver} act={act(q)} />,
        }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={quotes.length ? 'Không có báo giá ở mục này' : 'Chưa có báo giá cho khách này'} /> }}
      />
      {shown.length > 0 && <div className="-mt-1.5 text-[11.5px] text-gray-400">Bấm vào một dòng để xem kênh gửi và thao tác.</div>}

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 pt-1">
        <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">Cọc{deposits.length ? ` (${deposits.length})` : ''}</div>
        <span className="text-[11.5px] text-gray-400">Kế toán xác nhận đã nhận cọc → đẩy qua POS thành đơn hàng cọc; khách đến POS trừ cọc</span>
      </div>
      <Table<Quote>
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={deposits}
        columns={depositColumns(readOnly, canConfirm, act)}
        scroll={{ x: 700 }}
        className={TABLE}
        rowClassName={(q) => (isClosed(q) ? 'text-gray-500' : '')}
        locale={{ emptyText: <span className="text-[12.5px] text-gray-400">Chưa có yêu cầu cọc.</span> }}
      />

      <div className="text-[11.5px] text-gray-500">
        Báo giá đã gửi không sửa được — muốn đổi thì “Tạo bản mới”, bản cũ thành “Đã thay thế”. Huỷ phải có lý do. Telesales không thấy giá vốn và không có nút “đã nhận cọc”.
        {serverLead && ' Nút Gửi chỉ ghi nhận trên hệ thống, chưa tự gửi tin cho khách; báo giá hẹn giờ đến lúc phải bấm gửi.'}
      </div>
    </div>
  );
}
